/**
 * Runtime configuration.
 *
 * The same source builds the image for QA, UAT and production, and one running container
 * serves every customer hostname. Neither can be expressed by a value baked in at build
 * time, so the SPA knows nothing until it asks: it fetches `/config.json` from its own
 * origin before first render and reads everything environment-specific from the response.
 *
 * Nothing in `src/` may read `import.meta.env`. `npm run build` enforces that.
 */

/**
 * How the SPA signs people in. `disabled` is for local runs: nobody signs in, a banner says so,
 * and the API must be in its matching mode (Compose sets both; under `npm run dev`, match by hand).
 */
export type AuthMode = 'b2c' | 'disabled'

/** Sign-in settings, resolved from the flat `auth*` keys `/config.json` returns. */
export interface AuthConfig {
  /** Which mode the deployment is in. */
  readonly mode: AuthMode
  /** Application (client) id of the SPA app registration. Empty when disabled. */
  readonly clientId: string
  /** Authority including the user flow or policy name. Empty when disabled. */
  readonly authority: string
  /** Authority hostname, which MSAL must be told to trust for B2C. Empty when disabled. */
  readonly knownAuthority: string
  /** API scopes to request, already split. Empty when disabled. */
  readonly apiScopes: readonly string[]
  /** Origin-relative path B2C redirects back to, and a sign-in hop's code comes back to. */
  readonly redirectPath: string
  /**
   * Origin of the environment's sign-in site, the only address B2C returns to, such as
   * `https://tms.qa.skillable.com`. Every other address signs in through it (SM-04). Empty when this
   * deployment signs in on every address itself, as local runs do.
   */
  readonly signInOrigin: string
}

/** Where browser logs go, resolved from the `datadogClientToken` key `/config.json` returns. */
export interface LoggingSettings {
  /** Datadog's browser client token, which is public by design. Empty sends nothing to Datadog. */
  readonly datadogClientToken: string
}

/** The shape `/config.json` returns. */
export interface RuntimeConfig {
  /**
   * Origin-relative base path for API calls. The UI container proxies it to the API's App Service
   * hostname, so the browser stays on its own origin and the API needs no CORS policy.
   */
  readonly apiBaseUrl: string
  /** Deployment environment name, for display and log correlation only. */
  readonly environment: string
  /** Version of the running UI image. */
  readonly applicationVersion: string
  /** Hostname this response was computed for. */
  readonly site: string
  /** Sign-in settings. */
  readonly auth: AuthConfig
  /** Browser log settings. */
  readonly logging: LoggingSettings
  /**
   * Origin of the content host that plays packages, such as `https://content.tms.qa.skillable.com`
   * (LCH-34). Empty where none is bound yet.
   */
  readonly contentOrigin: string
  /**
   * Where each package plays on an origin of its own under the content host, so no package reads
   * another's localStorage or IndexedDB (110861): `contentOrigin` with `*.` before its host, such as
   * `https://*.content.tms.qa.skillable.com`, the `*` standing for the package's id. Empty where
   * every package plays on `contentOrigin` itself.
   */
  readonly contentPackageOrigins: string
}

/** Raised when `/config.json` cannot be fetched or does not have the expected shape. */
export class RuntimeConfigError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = 'RuntimeConfigError'
  }
}

function readString(source: Record<string, unknown>, key: string): string {
  const value = source[key]
  if (typeof value !== 'string' || value.length === 0) {
    throw new RuntimeConfigError(`/config.json is missing a non-empty "${key}".`)
  }
  return value
}

/** Reads a key required only in `b2c` mode. In `disabled` mode it may be empty or absent. */
function readAuthString(source: Record<string, unknown>, key: string, mode: AuthMode): string {
  if (mode === 'disabled') {
    const value = source[key]
    return typeof value === 'string' ? value : ''
  }
  return readString(source, key)
}

/**
 * Narrows the flat `auth*` keys into {@link AuthConfig}. Flat because nginx emits `/config.json`
 * as one `return 200 '…'` literal, where nesting is fragile.
 */
function parseAuthConfig(source: Record<string, unknown>): AuthConfig {
  const rawMode = readString(source, 'authMode')
  if (rawMode !== 'b2c' && rawMode !== 'disabled') {
    throw new RuntimeConfigError(
      `/config.json has authMode "${rawMode}". It must be "b2c" or "disabled".`,
    )
  }

  const mode: AuthMode = rawMode

  const authority = readAuthString(source, 'authAuthority', mode)
  const knownAuthority = readAuthString(source, 'authKnownAuthority', mode)
  // Any whitespace: a pasted tab or line break would otherwise yield one scope MSAL rejects.
  const apiScopes = readAuthString(source, 'authApiScopes', mode)
    .split(/\s+/)
    .filter((scope) => scope.length > 0)

  if (mode === 'b2c') {
    checkAuthority(authority, knownAuthority)

    // Whitespace alone passes the non-empty check but splits to nothing.
    if (apiScopes.length === 0) {
      throw new RuntimeConfigError('/config.json has an authApiScopes with no scopes in it.')
    }
  }

  return {
    mode,
    clientId: readAuthString(source, 'authClientId', mode),
    authority,
    knownAuthority,
    apiScopes,
    // Required in both modes: the callback route exists either way.
    redirectPath: readRedirectPath(source),
    // Hop ids and typed addresses go there: plain http only on this machine.
    signInOrigin: readOrigin(source, 'authSignInOrigin', 'https://tms.skillable.com', { isHttpLocalOnly: true }),
  }
}

/** Reads a key nginx always serves but that may be empty. */
function readOptionalString(source: Record<string, unknown>, key: string): string {
  const value = source[key]
  if (typeof value !== 'string') {
    throw new RuntimeConfigError(`/config.json is missing "${key}".`)
  }
  return value
}

/** Narrows `datadogClientToken` into {@link LoggingSettings}. */
function parseLoggingSettings(source: Record<string, unknown>): LoggingSettings {
  const token = readOptionalString(source, 'datadogClientToken').trim()

  return {
    // App Service passes a Key Vault reference through as written when it cannot resolve it,
    // such as before the secret exists: that is no token, not one.
    datadogClientToken: token.startsWith('@Microsoft.KeyVault(') ? '' : token,
  }
}

/** Reads `contentOrigin`: empty, or a bare http(s) origin with no path. */
function readContentOrigin(source: Record<string, unknown>): string {
  return readOrigin(source, 'contentOrigin', 'https://content.tms.skillable.com', { isRequired: true })
}

/** Reads `contentPackageOrigins`: empty or absent, or `contentOrigin` with `*.` before its host. */
function readContentPackageOrigins(source: Record<string, unknown>, contentOrigin: string): string {
  const value = source['contentPackageOrigins'] === undefined ? '' : readOptionalString(source, 'contentPackageOrigins')
  if (value === '') {
    return value
  }

  const content = contentOrigin === '' ? null : new URL(contentOrigin)
  if (content === null || value !== `${content.protocol}//*.${content.host}`) {
    throw new RuntimeConfigError(
      `/config.json has contentPackageOrigins "${value}". It must be empty, or contentOrigin with *. before its host, `
        + 'such as https://*.content.tms.skillable.com for https://content.tms.skillable.com.',
    )
  }
  return value
}

/**
 * Reads an origin key: empty, or a bare http(s) origin with no path; with `isHttpLocalOnly`, http
 * only on localhost. A key that is not `isRequired` reads as empty when absent.
 */
function readOrigin(
  source: Record<string, unknown>,
  key: string,
  example: string,
  { isRequired = false, isHttpLocalOnly = false }: { readonly isRequired?: boolean; readonly isHttpLocalOnly?: boolean } = {},
): string {
  const value = !isRequired && source[key] === undefined ? '' : readOptionalString(source, key)
  if (value === '') {
    return value
  }

  let url: URL | null = null
  try {
    url = new URL(value)
  } catch {
    // Reported below.
  }
  const isAllowedProtocol = url !== null && (url.protocol === 'https:' || (url.protocol === 'http:' && (!isHttpLocalOnly || isLocalHost(url.hostname))))
  if (url === null || !isAllowedProtocol || url.origin !== value) {
    throw new RuntimeConfigError(
      `/config.json has ${key} "${value}". It must be an origin such as `
        + `${example}, with no path or trailing slash, or empty`
        + `${isHttpLocalOnly ? ', and https unless it is localhost' : ''}.`,
    )
  }
  return value
}

/**
 * Whether a host name is this machine's own, as the API's `BaseUrlForm` allows plain http for a
 * site's base URL: `localhost` alone, so the sign-in site is always an address the API can hold.
 */
function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost'
}

/** Refuses an authority that MSAL, or the API (which appends /v2.0), would reject later. */
function checkAuthority(authority: string, knownAuthority: string): void {
  let url: URL
  try {
    url = new URL(authority)
  } catch {
    throw new RuntimeConfigError(`/config.json has authAuthority "${authority}", which is not a URL.`)
  }

  if (url.protocol !== 'https:') {
    throw new RuntimeConfigError(`/config.json has authAuthority "${authority}". It must be https.`)
  }

  // The raw string, because the URL parser drops a default ":443" and an empty "?".
  if (/^https:\/\/[^/?#]*[:@]/i.test(authority) || /[?#]/.test(authority)) {
    throw new RuntimeConfigError(
      `/config.json has authAuthority "${authority}". It must not name a port, credentials, a `
        + 'query string or a fragment.',
    )
  }

  if (/\/v2\.0$/i.test(url.pathname)) {
    throw new RuntimeConfigError(
      `/config.json has authAuthority "${authority}". Remove the trailing /v2.0: that form is `
        + "the API's authority, not the SPA's.",
    )
  }

  // The tenant, then the user flow: https://{tenant}.b2clogin.com/{tenant}.onmicrosoft.com/B2C_1_x
  if (url.pathname.split('/').filter((segment) => segment.length > 0).length < 2) {
    throw new RuntimeConfigError(
      `/config.json has authAuthority "${authority}", which names no user flow. It must end in `
        + 'the tenant and then the flow, for example /{tenant}.onmicrosoft.com/B2C_1_signupsignin.',
    )
  }

  if (url.pathname.endsWith('/') || url.pathname.includes('//')) {
    throw new RuntimeConfigError(
      `/config.json has authAuthority "${authority}". Remove the trailing or doubled slash.`,
    )
  }

  if (url.host.toLowerCase() !== knownAuthority.toLowerCase()) {
    throw new RuntimeConfigError(
      `/config.json has authKnownAuthority "${knownAuthority}", but the authority's host is `
        + `"${url.host}". They must be the same, or MSAL treats the authority as untrusted.`,
    )
  }
}

/**
 * Reads `authRedirectPath`, refusing anything but a plain path on this origin. It feeds
 * `new URL(path, origin)`, so `//host` or an absolute URL would send sign-in responses elsewhere.
 */
function readRedirectPath(source: Record<string, unknown>): string {
  const value = readString(source, 'authRedirectPath')

  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    throw new RuntimeConfigError(
      `/config.json has authRedirectPath "${value}". It must be a path on this site, `
        + 'beginning with a single "/" — a value that resolves to another origin would send '
        + 'the sign-in response away from this application.',
    )
  }

  if (value.includes('\\')) {
    throw new RuntimeConfigError(
      `/config.json has authRedirectPath "${value}". Backslashes are not allowed: browsers `
        + 'normalize them to forward slashes, so a value carrying one does not mean what it '
        + 'reads as.',
    )
  }

  // Also the callback's route in App.tsx: `?`/`#` never match and `:`/`*` are pattern syntax.
  if (/[?#:*]/.test(value)) {
    throw new RuntimeConfigError(
      `/config.json has authRedirectPath "${value}". It must be a plain path, with no query, `
        + 'fragment, ":" or "*" — it is the callback\'s route as well as its redirect URI.',
    )
  }

  if (value.includes('%')) {
    throw new RuntimeConfigError(
      `/config.json has authRedirectPath "${value}". It must not be percent-encoded: the `
        + 'router decodes the address before matching, so an encoded route is never matched.',
    )
  }

  // nginx embeds this in a single-quoted string, where `'` ends it and `$` expands a variable.
  if (/['$]/.test(value)) {
    throw new RuntimeConfigError(
      `/config.json has authRedirectPath "${value}". It must not contain "'" or "$": the `
        + 'container serves this value through nginx, which cannot carry either unchanged.',
    )
  }

  // `new URL` normalizes the redirect URI but the router registers the value as written.
  if (new URL(value, 'https://callback.invalid').pathname !== value) {
    throw new RuntimeConfigError(
      `/config.json has authRedirectPath "${value}", which a browser rewrites to a different `
        + 'path. Remove any "." or ".." segments (including percent-encoded ones) and any '
        + 'characters that need encoding.',
    )
  }

  // A path another route owns would shadow that route or the callback.
  const normalized = value.replace(/\/+$/, '').toLowerCase() || '/'
  if (
    reservedRoutePaths.has(normalized)
    || reservedRoutePrefixes.some((prefix) => `${normalized}/`.startsWith(prefix))
  ) {
    throw new RuntimeConfigError(
      `/config.json has authRedirectPath "${value}", which is already a route in this `
        + 'application or is served by nginx before the application sees it. Use a path of its '
        + 'own, such as "/auth/callback".',
    )
  }

  return value
}

/** Routes App.tsx declares itself, which the sign-in callback therefore cannot take. */
const reservedRoutePaths: ReadonlySet<string> = new Set([
  '/',
  '/login',
  '/ui-gallery',
  '/ui-gallery/rich-text',
  '/index.html',
  // The sign-in site's pass-through pages (SM-04), the app's own routes.
  '/auth/hop',
  '/auth/sign-out',
  // Answered by nginx itself (default.conf.template), so the SPA never sees a request there.
  '/healthz',
  '/config.json',
  '/swagger',
])

/** Prefixes nginx serves without the SPA (default.conf.template): proxied to the API or from disk. */
const reservedRoutePrefixes: readonly string[] = ['/api/', '/swagger/', '/assets/']

/**
 * Narrows an unknown payload to a {@link RuntimeConfig}.
 *
 * Validation is deliberate rather than a cast: a malformed `/config.json` is a deployment
 * fault, and failing here with a clear message beats every call site failing later with
 * `undefined`.
 *
 * @param payload - The parsed response body.
 * @returns The validated configuration.
 */
export function parseRuntimeConfig(payload: unknown): RuntimeConfig {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    throw new RuntimeConfigError('/config.json did not return a JSON object.')
  }

  const source = payload as Record<string, unknown>
  const contentOrigin = readContentOrigin(source)
  return {
    apiBaseUrl: readString(source, 'apiBaseUrl'),
    environment: readString(source, 'environment'),
    applicationVersion: readString(source, 'applicationVersion'),
    site: readString(source, 'site'),
    auth: parseAuthConfig(source),
    logging: parseLoggingSettings(source),
    contentOrigin,
    contentPackageOrigins: readContentPackageOrigins(source, contentOrigin),
  }
}

/**
 * Fetches and validates the runtime configuration.
 *
 * @param signal - Aborts the fetch.
 * @returns The validated configuration.
 */
export async function loadRuntimeConfig(signal?: AbortSignal): Promise<RuntimeConfig> {
  let response: Response
  try {
    response = await fetch('/config.json', { cache: 'no-store', signal: signal ?? null })
  } catch (cause) {
    throw new RuntimeConfigError('Could not reach /config.json.', { cause })
  }

  if (!response.ok) {
    throw new RuntimeConfigError(`/config.json responded ${String(response.status)}.`)
  }

  try {
    return parseRuntimeConfig(await response.json())
  } catch (cause) {
    if (cause instanceof RuntimeConfigError) {
      throw cause
    }
    throw new RuntimeConfigError('/config.json did not contain valid JSON.', { cause })
  }
}
